/**
 * WebSocket client for AgentDO.
 *
 * Connects to the agent session, sends prompts, receives events,
 * and handles reconnection with replay via the seq protocol.
 */

import WebSocket from 'ws';
import chalk from 'chalk';
import type { AgentEvent, ClientMessage } from '@spacecode/shared/agent';
import { renderEvent } from './renderer.js';

const MAX_RECONNECT = 10;
const BASE_DELAY_MS = 1000;

export function formatReconnectMessage(attempt: number, max: number): string {
  return chalk.yellow(`↻ Reconnecting (${attempt}/${max})...`);
}

export function formatReconnectSuccess(): string {
  return chalk.green('✓ Reconnected');
}

export interface AgentClientOptions {
  wsUrl: string;
  onEvent?: (event: AgentEvent) => void;
  onDone?: (success: boolean) => void;
  onError?: (message: string) => void;
  /** Fires when the agent finishes a prompt (done/result event) — used by the REPL to show the next prompt. */
  onReady?: () => void;
}

export class AgentClient {
  private ws: WebSocket | null = null;
  private lastSeq: number = 0;
  private reconnectAttempts: number = 0;
  private intentionalClose: boolean = false;
  private options: AgentClientOptions;

  constructor(options: AgentClientOptions) {
    this.options = options;
  }

  /** Connect to the agent WebSocket. Returns when the connection is established. */
  async connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.ws = new WebSocket(this.options.wsUrl);

      this.ws.on('open', () => {
        if (this.reconnectAttempts > 0) {
          process.stderr.write(formatReconnectSuccess() + '\n');
        }
        this.reconnectAttempts = 0;

        // If reconnecting, request replay of missed events
        if (this.lastSeq > 0) {
          this.send({ type: 'reconnect', last_seq: this.lastSeq });
        }

        resolve();
      });

      this.ws.on('message', (data: WebSocket.Data) => {
        try {
          const event = JSON.parse(data.toString()) as AgentEvent;
          this.lastSeq = event.seq;

          // Render to terminal
          renderEvent(event);

          // Forward to callback
          this.options.onEvent?.(event);

          if (event.type === 'ping') {
            this.send({ type: 'pong' });
            return;
          }

          if (event.type === 'done') {
            this.options.onReady?.();
            this.options.onDone?.(event.success);
          } else if (event.type === 'result') {
            this.options.onReady?.();
          } else if (event.type === 'error') {
            this.options.onError?.(event.message);
          }
        } catch {
          // Ignore malformed messages
        }
      });

      this.ws.on('close', () => {
        if (!this.intentionalClose) {
          this.attemptReconnect();
        }
      });

      this.ws.on('error', (err) => {
        if (this.reconnectAttempts === 0) {
          reject(err);
        }
      });
    });
  }

  /** Send a prompt to the agent. */
  prompt(text: string): void {
    this.send({ type: 'prompt', text });
  }

  /** Cancel the current agent run. */
  cancel(): void {
    this.send({ type: 'cancel' });
  }

  /** Close the connection cleanly. */
  close(): void {
    this.intentionalClose = true;
    this.ws?.close();
  }

  /** Send a raw message to the agent WebSocket. */
  send(msg: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= MAX_RECONNECT) {
      this.options.onError?.('Max reconnection attempts exceeded');
      return;
    }

    this.reconnectAttempts++;
    process.stderr.write(formatReconnectMessage(this.reconnectAttempts, MAX_RECONNECT) + '\n');
    const delay = BASE_DELAY_MS * Math.pow(2, this.reconnectAttempts - 1);

    setTimeout(() => {
      this.connect().catch(() => {
        this.attemptReconnect();
      });
    }, delay);
  }
}
